import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';

import type { ThemeMode } from '../types';

import { UserPreferencesService } from './user-preferences.service';

const DARK_CLASS = 'app-dark';
const DARK_QUERY = '(prefers-color-scheme: dark)';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly preferences = inject(UserPreferencesService);

  /** Tracks the OS setting live, so `system` follows a scheduled light/dark switch mid-session. */
  private readonly systemDark = signal<boolean>(this.watchSystemScheme());

  readonly mode = computed<ThemeMode>(() => this.preferences.theme());
  readonly isDark = computed(() => {
    const mode = this.mode();
    return mode === 'system' ? this.systemDark() : mode === 'dark';
  });

  constructor() {
    this.document.documentElement.classList.toggle(DARK_CLASS, this.isDark());
    effect(() => {
      this.document.documentElement.classList.toggle(DARK_CLASS, this.isDark());
    });
  }

  /** The topbar toggle flips what is on screen, which leaves `system` for an explicit choice. */
  toggle(): void {
    this.preferences.set('theme', this.isDark() ? 'light' : 'dark');
  }

  set(mode: ThemeMode): void {
    this.preferences.set('theme', mode);
  }

  private watchSystemScheme(): boolean {
    const view = this.document.defaultView;
    if (!view || typeof view.matchMedia !== 'function') return false;
    const query = view.matchMedia(DARK_QUERY);
    query.addEventListener('change', (event) => this.systemDark.set(event.matches));
    return query.matches;
  }
}
